import { supabase } from '../lib/supabase';

/**
 * 시스템 프롬프트 업데이트 함수 (관리자용)
 */
export const updateSystemPrompt = async (promptId, updates) => {
  try {
    // 현재 사용자 정보 가져오기
    const { data: { user } } = await supabase.auth.getUser();
    
    console.log('프롬프트 업데이트:', { promptId, updates });
    
    const updateData = {
      ...updates,
      updated_at: new Date().toISOString(),
      updated_by: user?.email || 'admin'
    };
    
    const { data, error } = await supabase
      .from('system_prompts')
      .update(updateData)
      .eq('id', promptId)
      .select();
    
    if (error) {
      console.error('Supabase 업데이트 오류:', error);
      throw error;
    }
    
    console.log('프롬프트 업데이트 성공:', data[0]);
    return data[0];
    
  } catch (error) {
    console.error('프롬프트 업데이트 실패:', error);
    throw error;
  }
};

/**
 * 시스템 프롬프트 삭제 함수 (관리자용)
 * 주의: 실제 삭제가 아닌 soft delete (비활성화)
 */
export const deleteSystemPrompt = async (promptId) => {
  try {
    // 현재 사용자 정보 가져오기
    const { data: { user } } = await supabase.auth.getUser();
    
    console.log('프롬프트 삭제 요청:', promptId);
    
    // 실제 삭제 대신 비활성화 및 삭제 표시
    const { data, error } = await supabase
      .from('system_prompts')
      .update({
        is_active: false,
        deactivated_at: new Date().toISOString(),
        deleted_at: new Date().toISOString(),
        deleted_by: user?.email || 'admin'
      })
      .eq('id', promptId)
      .select();
    
    if (error) {
      console.error('Supabase 삭제 오류:', error);
      throw error;
    }
    
    console.log('프롬프트 삭제 성공:', data[0]);
    return data[0];
    
  } catch (error) {
    console.error('프롬프트 삭제 실패:', error);
    throw error;
  }
};

/**
 * 시스템 프롬프트 복제 함수 (관리자용)
 */
export const duplicateSystemPrompt = async (promptId, newVersion) => {
  try {
    // 원본 프롬프트 가져오기
    const { data: originalPrompt, error: fetchError } = await supabase
      .from('system_prompts')
      .select('*')
      .eq('id', promptId)
      .single();
    
    if (fetchError) throw fetchError;
    
    // 새 버전으로 복제
    const { data, error } = await createSystemPromptVersion(
      originalPrompt.name,
      newVersion,
      originalPrompt.content,
      `${originalPrompt.description} (복제됨)`,
      originalPrompt.target_user_group
    );
    
    if (error) throw error;
    
    console.log('프롬프트 복제 성공:', data);
    return data;
    
  } catch (error) {
    console.error('프롬프트 복제 실패:', error);
    throw error;
  }
};

/**
 * 특정 프롬프트 조회 함수 (관리자용)
 */
export const getSystemPromptById = async (promptId) => {
  try {
    const { data, error } = await supabase
      .from('system_prompts')
      .select('*')
      .eq('id', promptId)
      .single();
    
    if (error) throw error;
    
    return data;
    
  } catch (error) {
    console.error('프롬프트 조회 실패:', error);
    throw error;
  }
};

/**
 * 사용자에게 할당할 프롬프트 버전을 결정하는 함수
 * 사용자 역할에 따라 그룹 결정
 */
const getUserGroup = async (userId) => {
  // userId가 없으면 기본값
  if (!userId) return 'all';
  
  try {
    // 현재 사용자의 역할 확인
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user || user.id !== userId) {
      return 'general'; // 기본값은 일반 사용자
    }
    
    // 관리자인지 확인
    const { data: isAdminData, error: adminError } = await supabase.rpc('is_admin');
    
    if (!adminError && isAdminData === true) {
      return 'admin'; // 관리자 그룹
    }
    
    // 관리자가 아니면 일반 사용자
    return 'general';
    
  } catch (error) {
    console.error('사용자 그룹 확인 중 오류:', error);
    return 'general'; // 오류 시 기본값은 일반 사용자
  }
};

/**
 * 활성화된 시스템 프롬프트를 가져오는 함수
 */
export const getActiveSystemPrompt = async (userId = null, promptName = 'latteis_main') => {
  try {
    
    // 사용자 그룹 결정
    const userGroup = await getUserGroup(userId);
    
    // admin 사용자인 경우, admin 프롬프트 우선 확인
    if (userGroup === 'admin') {
      const { data: adminPrompt, error: adminError } = await supabase
        .from('system_prompts')
        .select('*')
        .eq('name', promptName)
        .eq('is_active', true)
        .eq('target_user_group', 'admin')
        .order('created_at', { ascending: false })
        .limit(1);
      
      if (!adminError && adminPrompt && adminPrompt.length > 0) {
        return {
          ...adminPrompt[0],
          isFromDB: true
        };
      }
    }
    
    // admin 프롬프트가 없거나 admin이 아닌 경우, 일반 프롬프트 확인
    const { data, error } = await supabase
      .from('system_prompts')
      .select('*')
      .eq('name', promptName)
      .eq('is_active', true)
      .in('target_user_group', ['all', userGroup])
      .neq('target_user_group', 'admin') // admin 프롬프트는 제외 (일반 사용자가 admin 프롬프트를 받지 않도록)
      .order('created_at', { ascending: false })
      .limit(1);
    
    if (error) {
      console.error('프롬프트 가져오기 실패:', error);
      // 데이터베이스 오류 시 기본 프롬프트 사용
      console.log('기본 프롬프트 사용');
      return {
        id: 0, // fallback ID (만약의 경우를 위해 0으로 설정)
        name: promptName,
        version: 'fallback-1.0',
        content: '당신은 Latte AI 컨설턴트입니다. 사용자의 이력서 작성을 도와주세요.',
        target_user_group: 'all',
        is_active: true,
        isFromDB: false
      };
    }
    
    if (!data || data.length === 0) {
      console.warn('활성화된 프롬프트가 없음 - 기본 프롬프트 사용');
      return {
        id: 0, // fallback ID (만약의 경우를 위해 0으로 설정)
        name: promptName,
        version: 'fallback-1.0',
        content: '당신은 Latte AI 컨설턴트입니다. 사용자의 이력서 작성을 도와주세요.',
        target_user_group: 'all',
        is_active: true,
        isFromDB: false
      };
    }
    
    return {
      ...data[0],
      isFromDB: true
    };
    
  } catch (error) {
    console.error('프롬프트 서비스 에러:', error);
    // 에러 시 기본 프롬프트 사용
    console.log('에러 대응 - 기본 프롬프트 사용');
    return {
      id: 0, // fallback ID (만약의 경우를 위해 0으로 설정)
      name: 'latteis_main',
      version: 'fallback-1.0',
      content: '당신은 Latte AI 컨설턴트입니다. 사용자의 이력서 작성을 도와주세요.',
      target_user_group: 'all',
      is_active: true,
      isFromDB: false
    };
  }
};

/**
 * 새로운 시스템 프롬프트 버전을 생성하는 함수 (관리자용)
 */
export const createSystemPromptVersion = async (name, version, content, description, targetUserGroup = 'all') => {
  try {
    // 현재 사용자 정보 가져오기
    const { data: { user } } = await supabase.auth.getUser();
    
    console.log('현재 사용자:', user?.email);
    console.log('삽입 데이터:', { name, version, content: content.substring(0, 100) + '...', description, targetUserGroup });
    
    const { data, error } = await supabase
      .from('system_prompts')
      .insert([{
        name,
        version,
        content,
        description,
        target_user_group: targetUserGroup,
        created_by: user?.email || 'admin',
        is_active: false
      }])
      .select();
    
    if (error) {
      console.error('Supabase 오류 상세:', error);
      console.error('오류 코드:', error.code);
      console.error('오류 메시지:', error.message);
      console.error('오류 세부사항:', error.details);
      throw error;
    }
    
    console.log('성공적으로 생성된 프롬프트:', data[0]);
    return data[0];
    
  } catch (error) {
    console.error('프롬프트 버전 생성 실패:', error);
    throw error;
  }
};

/**
 * 특정 프롬프트 버전을 활성화하는 함수 (관리자용)
 */
export const activateSystemPromptVersion = async (promptId, promptName) => {
  try {
    // 먼저 활성화할 프롬프트의 정보를 가져옴
    const { data: promptToActivate, error: fetchError } = await supabase
      .from('system_prompts')
      .select('target_user_group')
      .eq('id', promptId)
      .single();
    
    if (fetchError) throw fetchError;
    
    // admin 그룹이 아닌 경우에만 같은 그룹의 다른 프롬프트 비활성화
    if (promptToActivate.target_user_group !== 'admin') {
      // 같은 이름과 같은 target_user_group의 모든 프롬프트 비활성화
      const { error: deactivateError } = await supabase
        .from('system_prompts')
        .update({ 
          is_active: false, 
          deactivated_at: new Date().toISOString() 
        })
        .eq('name', promptName)
        .eq('target_user_group', promptToActivate.target_user_group)
        .eq('is_active', true);
      
      if (deactivateError) throw deactivateError;
    } else {
      // admin 그룹인 경우, admin 그룹 내에서만 비활성화
      const { error: deactivateError } = await supabase
        .from('system_prompts')
        .update({ 
          is_active: false, 
          deactivated_at: new Date().toISOString() 
        })
        .eq('name', promptName)
        .eq('target_user_group', 'admin')
        .eq('is_active', true);
      
      if (deactivateError) throw deactivateError;
    }
    
    // 2. 선택된 프롬프트 활성화
    const { data, error: activateError } = await supabase
      .from('system_prompts')
      .update({ 
        is_active: true, 
        activated_at: new Date().toISOString() 
      })
      .eq('id', promptId)
      .select();
    
    if (activateError) throw activateError;
    return data[0];
    
  } catch (error) {
    console.error('프롬프트 활성화 실패:', error);
    throw error;
  }
};

/**
 * 모든 시스템 프롬프트 버전 목록을 가져오는 함수 (관리자용)
 * 삭제된 프롬프트는 제외
 */
export const getAllSystemPrompts = async (promptName = 'latteis_main') => {
  try {
    const { data, error } = await supabase
      .from('system_prompts')
      .select(`
        *,
        consulting_sessions (
          id
        )
      `)
      .eq('name', promptName)
      .is('deleted_at', null) // 삭제되지 않은 것만
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    
    // 세션 수 추가
    return data.map(prompt => {
      const sessions = prompt.consulting_sessions || [];
      return {
        ...prompt,
        sessionCount: sessions.length
      };
    });
    
  } catch (error) {
    console.error('프롬프트 목록 가져오기 실패:', error);
    throw error;
  }
};

/**
 * 프롬프트 사용 통계를 가져오는 함수 (관리자용)
 */
export const getPromptUsageStats = async (promptName = 'latteis_main') => {
  try {
    const { data, error } = await supabase
      .from('system_prompts')
      .select(`
        id,
        name,
        version,
        target_user_group,
        is_active,
        created_at,
        activated_at,
        consulting_sessions (
          id
        )
      `)
      .eq('name', promptName)
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    
    // 세션 수만 계산
    return data.map(prompt => {
      const sessions = prompt.consulting_sessions || [];
      
      return {
        ...prompt,
        sessionCount: sessions.length
      };
    });
    
  } catch (error) {
    console.error('프롬프트 통계 가져오기 실패:', error);
    throw error;
  }
};

